import {
  CLASSIFIER_GUIDANCE_MAX_LENGTH,
  VIOLATION_ACTIONS,
  VIOLATION_SEVERITIES,
} from '@aysar/contracts';

import {
  type FieldDef,
  type FieldOption,
} from '@/shared/components/dynamic-form/types/dynamic-form.types';

/** Translates a key, with interpolation. The caller's `t`. */
export type Translate = (key: string, params?: Record<string, unknown>) => string;

/**
 * The rule form.
 *
 * ## `key` is locked after creation
 *
 * It is the label handed to the classifier **and** the join every historical case
 * was judged under, so the update contract omits it entirely. The field stays on
 * screen, disabled, rather than vanishing — an edit form that shows fewer fields
 * than the create form makes staff wonder what they lost.
 *
 * ## `suspensionDays` is always visible, and that is deliberate
 *
 * The obvious design is `visibleWhen: action === 'suspend'`. It cannot be used:
 * the kit **strips a hidden field before its resolver runs**, and
 * `ViolationTypeCreateSchema` requires the key (nullable, not optional), so a
 * `warn` rule would fail validation on a control the operator never saw. Nor can
 * the contract carry a `.default(null)` — `useDynamicFormLogic` refuses a schema
 * with one, because the form would then edit one shape while the resolver
 * validated another, and `.partial()` does not suppress a default, so the update
 * schema would start writing `null` over live suspension lengths.
 *
 * So the control stays, with a description that says exactly when it applies and
 * an `invalidMessage` carrying the contract's own rule in this module's words.
 * An empty box is `null` (`isEmptyNull`), which is what a non-suspend rule wants.
 *
 * ## The guidance is the biggest field on the form, because it is the product
 *
 * `classifierGuidance` is copied verbatim into the model's label description —
 * editing it *is* retuning the classifier, which is what "changes behaviour
 * without deploy" means for this surface. It gets the full row.
 *
 * ## A behavioural rule locks two of its own controls (P27a3)
 *
 * A type whose key is a behavioural rule is raised by the detector, not the
 * classifier: `reviewBeforeEnforce` is always on (every flag queues for a
 * person, and the api refuses `false`) and the guidance is never sent to a
 * model. Both stay on screen, disabled, with the reason as their description —
 * a locked switch is still a switch, and a field that vanished would make an
 * operator wonder what they lost. Neither may use `visibleWhen`: the kit strips
 * a hidden field before the resolver runs, and the create schema requires both.
 */
export interface ViolationTypeFieldOptions {
  isEditing: boolean;
  /** The key being edited is one of `VIOLATION_BEHAVIOR_RULES`. */
  isBehavioral: boolean;
  templateKeyOptions: readonly FieldOption[];
}

export function buildViolationTypeFields(
  t: Translate,
  { isEditing, isBehavioral, templateKeyOptions }: ViolationTypeFieldOptions,
): FieldDef[] {
  return [
    {
      name: 'key',
      kind: 'slug',
      label: t('types.form.key'),
      description: t('types.form.keyHint'),
      sourceName: 'name',
      separator: '_',
      maxLength: 64,
      isRequired: true,
      isDisabled: isEditing,
      isLockedAfterCreate: true,
    },
    {
      name: 'name',
      kind: 'text',
      label: t('types.form.name'),
      description: t('types.form.nameHint'),
      isTranslatable: true,
      isRequired: true,
    },
    {
      name: 'description',
      kind: 'textarea',
      label: t('types.form.description'),
      description: t('types.form.descriptionHint'),
      isTranslatable: true,
      isRequired: true,
      colSpan: 'full',
    },
    {
      name: 'severity',
      kind: 'select',
      label: t('types.form.severity'),
      isRequired: true,
      options: VIOLATION_SEVERITIES.map((severity) => ({
        value: severity,
        label: t(`severity.${severity}`),
      })),
    },
    {
      name: 'action',
      kind: 'select',
      label: t('types.form.action'),
      description: t('types.form.actionHint'),
      isRequired: true,
      options: VIOLATION_ACTIONS.map((action) => ({
        value: action,
        label: t(`action.${action}`),
      })),
    },
    {
      name: 'suspensionDays',
      kind: 'number',
      label: t('types.form.suspensionDays'),
      description: t('types.form.suspensionDaysHint'),
      min: 1,
      max: 3650,
      // An empty box means `null`, not `''` and not absence: the contract wants
      // the key present and nullable, and only the field def knows which of the
      // three its contract wants.
      isEmptyNull: true,
      // The contract's `.refine` reaches the form as Zod's `custom` code with an
      // English constant; this is the one failure the kit cannot classify from
      // the code alone, because the reason is the rule rather than the shape.
      invalidMessage: t('types.form.suspensionDaysInvalid'),
    },
    {
      name: 'reviewBeforeEnforce',
      kind: 'switch',
      label: t('types.form.reviewBeforeEnforce'),
      description: t(
        isBehavioral
          ? 'types.form.reviewBeforeEnforceLocked'
          : 'types.form.reviewBeforeEnforceHint',
      ),
      isDisabled: isBehavioral,
    },
    {
      // A PICKER since P21b, and it closes P20b's own issue. The contract now
      // refines this against `isDeliverableTemplateKey`, so free text could only
      // ever produce a refusal the operator had no way to anticipate; the list
      // comes from the notification catalogue through the shared W6 bridge, and
      // carries this rule's own per-type key even though no catalogue entry
      // exists for it (it resolves through the key FAMILY).
      //
      // Not `isCreatable`: every key the contract accepts is already offered.
      name: 'legalNoticeTemplateKey',
      kind: 'combobox',
      label: t('types.form.legalNoticeTemplateKey'),
      description: t('types.form.legalNoticeTemplateKeyHint'),
      searchPlaceholder: t('types.form.templateKeySearch'),
      isRequired: true,
      options: templateKeyOptions,
      colSpan: 'full',
    },
    {
      name: 'classifierGuidance',
      kind: 'textarea',
      label: t('types.form.classifierGuidance'),
      description: t(
        isBehavioral ? 'types.form.classifierGuidanceUnused' : 'types.form.classifierGuidanceHint',
      ),
      isRequired: true,
      // Three rows for a sentence nobody edits; six for the prompt that is the product.
      rows: isBehavioral ? 3 : 6,
      maxLength: CLASSIFIER_GUIDANCE_MAX_LENGTH,
      isDisabled: isBehavioral,
      colSpan: 'full',
    },
    {
      name: 'isActive',
      kind: 'switch',
      label: t('types.form.isActive'),
      description: t('types.form.isActiveHint'),
    },
  ];
}
